/**
 * ScanBadgeStep — Badge scanning step for MobileClockIn.
 * Shows product info card, material info card, current assignments,
 * and embedded badge input for scanning employee badges.
 * Extracted from MobileClockIn.tsx for maintainability.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatYield, isYieldFallback } from '../../../hooks/useYieldFactor';
import { WorkerAssignment } from '../../../services/operatorService';
import type { SessionInfo } from '../../../types';
import {
  Package,
  User,
  Loader2,
  Camera,
  ArrowLeft,
} from 'lucide-react';

interface ScanBadgeStepProps {
  session: SessionInfo;
  assignments: { operator: WorkerAssignment | null; packer: WorkerAssignment | null };
  embedded: boolean;
  manualInput: string;
  onManualInputChange: (value: string) => void;
  onManualSubmit: () => void;
  onShowCamera: () => void;
  onReset: () => void;
  isLoading: boolean;
  badgeInputRef: React.RefObject<HTMLInputElement>;
}

const ScanBadgeStep: React.FC<ScanBadgeStepProps> = ({
  session,
  assignments,
  embedded,
  manualInput,
  onManualInputChange,
  onManualSubmit,
  onShowCamera,
  onReset,
  isLoading,
  badgeInputRef,
}) => {
  const { t } = useLanguage();

  // Use backend-calculated expected_boxes directly
  const targetOutput = Number(session.target_output_weight) || 0;
  const calculatedCases = session.expected_boxes || 0;
  const boxesPacked = session.boxes_produced || 0;

  return (
    <div className="flex-1 flex flex-col">
      {/* Back button — returns to scan_station (only shown when embedded in Layout) */}
      {embedded && (
        <button
          onClick={onReset}
          className="mb-2 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors self-start"
          title={t('common.back') || 'Back'}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      )}

      {/* Product Information Card - Purple gradient like Session Details */}
      <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 dark:from-indigo-900/40 dark:to-purple-900/40 rounded-xl p-4 md:p-5 lg:p-6 mb-3 border border-indigo-300/50 dark:border-indigo-700/50">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('sessionDetails.productInfo') || 'Product Information'}
          </h3>
        </div>
        {/* Product header - SKU, English name, Chinese name on one line */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-3">
          <span className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{session.product_sku}</span>
          {session.product_name && (
            <span className="text-sm md:text-base text-gray-700 dark:text-gray-300">{session.product_name}</span>
          )}
          {session.product_chinese && (
            <span className="text-sm md:text-base text-gray-700 dark:text-gray-300">{session.product_chinese}</span>
          )}
        </div>
        {/* Product details - 3 columns in one row */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('sessionDetails.estimatedOutput') || 'Est. Output'}</p>
            <p className="text-sm md:text-base font-medium text-gray-900 dark:text-white">
              {targetOutput > 0 ? `${targetOutput.toFixed(2)} lbs` : '-'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('sessionDetails.packing') || 'Packing'}</p>
            <p className="text-sm md:text-base font-medium text-gray-900 dark:text-white">{session.packing || '-'}</p>
          </div>
          <div>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('sessionDetails.estimatedCases') || 'Est. Cases'}</p>
            <p className="text-sm md:text-base font-medium text-gray-900 dark:text-white">{calculatedCases > 0 ? calculatedCases : '-'}</p>
          </div>
        </div>
        {/* Current Progress Bar */}
        {calculatedCases > 0 && (
          <div className="mt-3 pt-3 border-t border-indigo-300/30 dark:border-indigo-700/30">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.currentProgress') || 'Current Progress'}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {boxesPacked} / {calculatedCases} {t('sessionDetails.cases') || 'cases'}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
              <div
                className="bg-indigo-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, (boxesPacked / calculatedCases) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Material Information Card - Gray background like Session Details */}
      <div className="bg-gray-100 dark:bg-gray-700/50 rounded-xl p-4 md:p-5 lg:p-6 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('sessionDetails.materialInfo') || 'Material Information'}
          </h3>
        </div>
        {/* Material header with item number and names */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-2">
          <span className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{session.raw_material_item_number || '-'}</span>
          {session.raw_material_english && (
            <span className="text-sm md:text-base text-gray-600 dark:text-gray-300">{session.raw_material_english}</span>
          )}
          {session.raw_material_chinese && (
            <span className="text-sm md:text-base text-gray-600 dark:text-gray-300">{session.raw_material_chinese}</span>
          )}
        </div>
        {/* Brand and Yield Factor row */}
        <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-700 dark:text-gray-200 mb-3">
          <span>{t('material.brand') || 'Brand'}: <span className="font-medium">{session.raw_material_brand || '-'}</span></span>
          <span className="text-gray-400">|</span>
          <span>{t('material.yieldFactor') || 'Yield'}: {(() => {
            const yieldValue = Number(session.raw_material_yield) || 0;
            if (yieldValue > 0) {
              return <span className={`font-medium ${isYieldFallback(session.raw_material_yield_source) ? 'text-amber-600 dark:text-amber-400' : ''}`}>{formatYield(yieldValue, undefined, session.raw_material_yield_source)}</span>;
            } else {
              return <span className="font-medium text-gray-400 dark:text-gray-500">?</span>;
            }
          })()}</span>
        </div>
        {/* Lot details row */}
        <div className="grid grid-cols-3 gap-2 text-xs text-gray-500 dark:text-gray-400 border-l-2 border-gray-300 dark:border-gray-600 pl-3">
          <div>
            <p>{t('common.lotNumber') || 'Lot Number'}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{session.lot_number || '-'}</p>
          </div>
          <div>
            <p>{t('material.initialWeight') || 'Initial Weight'}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{session.initial_material_weight ? `${Number(session.initial_material_weight).toFixed(2)} lbs` : '-'}</p>
          </div>
          <div>
            <p>{t('supervisor.scheduleLocation') || 'Location'}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{session.material_location_code || '-'}</p>
          </div>
        </div>
      </div>

      {/* Current Assignments */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-4 shadow-sm">
        {/* Header row — with badge input embedded next to title on desktop */}
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            {t('mobile.currentAssignments') || 'Currently Working'}
          </h3>
          {/* Badge scan input — same amber style as mobile header, shown in this header row when embedded */}
          {embedded && (
            <div className="flex items-center gap-1.5 ml-auto">
              <div className="flex items-center gap-1.5 bg-amber-500/30 rounded-lg px-2 py-2 w-[140px] animate-pulse ring-2 ring-amber-400/50">
                <User className="w-4 h-4 text-amber-600 dark:text-amber-300 flex-shrink-0" />
                <input
                  ref={badgeInputRef}
                  type="text"
                  inputMode="none"
                  value={manualInput}
                  onChange={(e) => onManualInputChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onManualSubmit()}
                  placeholder="Badge"
                  className="flex-1 bg-transparent text-amber-900 dark:text-white placeholder-amber-400 dark:placeholder-white/70 outline-none text-sm min-w-0"
                  autoComplete="off"
                />
                {isLoading && <Loader2 className="w-4 h-4 animate-spin text-amber-500 dark:text-white/70 flex-shrink-0" />}
              </div>
              <button
                onClick={onShowCamera}
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 transition-colors"
                title={t('cameraScanner.openCamera') || 'Use Camera'}
              >
                <Camera className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className={`p-3 rounded-lg ${
            assignments.operator?.on_break
              ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
              : assignments.operator
                ? 'bg-green-50 dark:bg-green-900/20'
                : 'bg-gray-50 dark:bg-gray-700/30'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-500 uppercase">{t('mobile.operator') || 'Operator'}</span>
              {assignments.operator?.on_break && (
                <span className="text-xs text-amber-600 dark:text-amber-400">{t('clockInModal.onBreak') || 'On break'}</span>
              )}
            </div>
            <div className="font-medium md:text-base text-gray-900 dark:text-white truncate">
              {assignments.operator
                ? `${assignments.operator.employee_number}${assignments.operator.name ? ` - ${assignments.operator.name}` : ''}`
                : '-'}
            </div>
          </div>
          <div className={`p-3 rounded-lg ${
            assignments.packer?.on_break
              ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
              : assignments.packer
                ? 'bg-green-50 dark:bg-green-900/20'
                : 'bg-gray-50 dark:bg-gray-700/30'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-500 uppercase">{t('mobile.packer') || 'Packer'}</span>
              {assignments.packer?.on_break && (
                <span className="text-xs text-amber-600 dark:text-amber-400">{t('clockInModal.onBreak') || 'On break'}</span>
              )}
            </div>
            <div className="font-medium md:text-base text-gray-900 dark:text-white truncate">
              {assignments.packer
                ? `${assignments.packer.employee_number}${assignments.packer.name ? ` - ${assignments.packer.name}` : ''}`
                : '-'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanBadgeStep;
